<script setup lang="ts">
/**
 * Consistent page header: optional breadcrumb, title/subtitle, and an actions slot.
 * Used at the top of every screen instead of an ad-hoc `<h1>` + button row.
 */
import Icon from './Icon.vue';

defineProps<{
  title: string;
  subtitle?: string;
  breadcrumb?: { label: string; to?: unknown }[];
  symbol?: string;
}>();
</script>

<template>
  <header class="jv-page-header flex flex-col gap-3">
    <nav v-if="breadcrumb?.length" aria-label="breadcrumb" class="flex flex-wrap items-center gap-1 text-xs">
      <template v-for="(crumb, index) in breadcrumb" :key="index">
        <RouterLink
          v-if="crumb.to"
          :to="crumb.to as never"
          class="text-jv-on-surface-variant no-underline hover:text-jv-primary"
          >{{ crumb.label }}</RouterLink
        >
        <span v-else class="text-jv-on-surface-variant">{{ crumb.label }}</span>
        <Icon
          v-if="index < breadcrumb.length - 1"
          name="chevron_left"
          :size="14"
          class="ltr:rotate-180 text-jv-outline"
        />
      </template>
    </nav>
    <div class="flex flex-wrap items-center justify-between gap-4">
      <div class="flex min-w-0 flex-1 items-start gap-3">
        <span
          v-if="symbol"
          class="grid size-10 shrink-0 place-items-center rounded-jv-md bg-jv-primary-container text-jv-on-primary"
          ><Icon :name="symbol" :size="24"
        /></span>
        <div class="min-w-0">
          <h1 class="m-0 font-headline text-[28px] leading-9 font-semibold tracking-[-0.015em] text-jv-on-surface">
            {{ title }}
          </h1>
          <div v-if="$slots.subtitle" class="m-0 mt-1 text-sm text-jv-on-surface-variant">
            <slot name="subtitle" />
          </div>
          <p v-else-if="subtitle" class="m-0 mt-1 text-sm text-jv-on-surface-variant">{{ subtitle }}</p>
        </div>
      </div>
      <div v-if="$slots.actions" class="flex flex-wrap items-center gap-2">
        <slot name="actions" />
      </div>
    </div>
  </header>
</template>
